import { useTranslations } from '@vocab/react';
import {
  Autosuggest,
  filterSuggestions,
  IconSearch,
} from 'braid-design-system';
import { useEffect, useState } from 'react';

import translations from 'src/pages/ConnectedPartners/.vocab';
import type { Partner } from 'src/types';
import { analytics } from 'src/utils/analytics/analytics';
import { useQueryHistory } from 'src/utils/hooks/useQueryHistory';
import { slugify } from 'src/utils/slugify';

interface Props {
  disabled: boolean;
  groupedSuggestions: Array<{
    label: string;
    suggestions: Array<{ text: string; value: Partner }>;
  }>;
}

type AutosuggestValue = { text: string; value?: Partner };

export const PartnerFilter = ({
  disabled = false,
  groupedSuggestions,
}: Props) => {
  const { t } = useTranslations(translations);
  const {
    value: nameQuery,
    set: setNameQuery,
    delete: deleteNameQuery,
  } = useQueryHistory('name');
  const [autosuggestValue, setAutosuggestValue] = useState<AutosuggestValue>({
    text: nameQuery ?? '',
  });

  const onSearchValueChange = ({
    text,
    value: partner,
  }: {
    text: string;
    value?: Partner;
  }) => {
    setAutosuggestValue({ text, value: partner });

    if (partner) {
      setNameQuery(partner.name);
      analytics.link(
        `partner-filter-${partner.id ?? slugify({ str: partner.name })}`,
      );
    }
  };

  const clearSearch = () => {
    setAutosuggestValue({ text: '' });
    deleteNameQuery();
  };

  useEffect(() => {
    setAutosuggestValue({ text: nameQuery ?? '' });
  }, [nameQuery]);

  return (
    <Autosuggest<Partner>
      id="partner-filter"
      disabled={disabled}
      icon={<IconSearch />}
      label={t('searchByPartner')}
      value={autosuggestValue}
      onChange={onSearchValueChange}
      onClear={clearSearch}
      suggestions={filterSuggestions(groupedSuggestions, autosuggestValue.text)}
    />
  );
};
